import { useEffect, useRef } from 'react';
import { useSSE } from './useSSE';
import { useInvoiceFileProcessing } from 'src/use-cases/invoice/useInvoices';

/**
 * Hook for monitoring invoice upload processing status via SSE
 *
 * NEW ARCHITECTURE:
 * - SSE acts as a TRIGGER/NOTIFICATION only
 * - When SSE event received → refetch from API
 * - Components use API data (single source of truth)
 * - No more data merging, no more sync issues
 *
 * Flow:
 * 1. SSE event arrives → "something changed!"
 * 2. Immediately call API: /dev/auth-user/api/invoice-file-processing
 * 3. Use fresh API data in components
 */
export const useUploadProgress = (batchId: string | null, enabled: boolean = true) => {
  const { invoiceUpdates, isConnected, clearInvoiceUpdates } = useSSE();

  // API as single source of truth - no polling
  const { data, isLoading, refetch } = useInvoiceFileProcessing(
    batchId,
    !!batchId && enabled,
    false // No polling - SSE triggers refetch
  );

  /**
   * Throttle state: Track if a refetch is scheduled
   * This prevents multiple rapid API calls when notifications arrive back-to-back
   */
  const refetchTimerRef = useRef<NodeJS.Timeout | null>(null);
  const lastRefetchTimeRef = useRef<number>(0);

  /**
   * When SSE event received → Trigger API refetch with throttling
   *
   * THROTTLING LOGIC:
   * - Minimum 2 seconds between API calls
   * - If timer is running, skip the new call
   * - This prevents rapid-fire calls when multiple invoices process simultaneously
   */
  useEffect(() => {
    if (invoiceUpdates.length > 0 && batchId && enabled) {
      const now = Date.now();
      const timeSinceLastRefetch = now - lastRefetchTimeRef.current;
      const THROTTLE_DELAY = 2000; // 2 seconds minimum between calls

      // If there's already a pending refetch scheduled, skip this one
      if (refetchTimerRef.current !== null) {
        console.log('⏳ SSE: Refetch already scheduled, skipping duplicate call');
        return;
      }

      // If enough time has passed, refetch immediately
      if (timeSinceLastRefetch >= THROTTLE_DELAY) {
        console.log('📡 SSE event received → Refetching API data (immediate)');
        lastRefetchTimeRef.current = now;
        refetch();
      } else {
        // Schedule refetch after remaining throttle time
        const remainingTime = THROTTLE_DELAY - timeSinceLastRefetch;
        console.log(`⏰ SSE: Scheduling refetch in ${remainingTime}ms`);

        refetchTimerRef.current = setTimeout(() => {
          console.log('📡 SSE event received → Refetching API data (throttled)');
          lastRefetchTimeRef.current = Date.now();
          refetch();
          refetchTimerRef.current = null;
        }, remainingTime);
      }
    }

    // Cleanup timer on unmount or when dependencies change
    return () => {
      if (refetchTimerRef.current) {
        clearTimeout(refetchTimerRef.current);
        refetchTimerRef.current = null;
      }
    };
  }, [invoiceUpdates.length, batchId, enabled, refetch]);

  /**
   * Clear updates when batch changes
   */
  useEffect(() => {
    return () => {
      clearInvoiceUpdates();
    };
  }, [batchId, clearInvoiceUpdates]);

  return {
    // API data - single source of truth
    data,

    // Loading states
    isLoading,
    isConnected,

    // Actions
    refetch,
    clearInvoiceUpdates,

    // Debug
    debugInfo: {
      batchId,
      enabled,
      sseEventCount: invoiceUpdates.length,
      isConnected,
    },
  };
};
